import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Clock, ArrowRight, ListChecks, Hash, CloudMoon } from "lucide-react";
import Layout from "../components/Layout";
import Seo from "../components/Seo";
import ResultBox from "../components/ResultBox";
import CardGrid from "../components/CardGrid";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";
import { getTodayResult, getPreviousResults, getLatestCommonNumber } from "../lib/api";
import { todayDDMMYYYY } from "../lib/date";
import { DREAM_CHART } from "../data/dreamChart";

const FAQS = [
  [
    "What is the Bhutan Morning Teer result today?",
    "The Bhutan Morning Teer result today is the winning two-digit F/R and S/R number. It appears in the result card at the top of this page once it has been officially declared and published.",
  ],
  [
    "What time is the Bhutan Morning Teer result published?",
    "The First Round (F/R) result is published around 10:30 AM and the Second Round (S/R) result around 11:30 AM, after the archery round is completed and the arrows are counted.",
  ],
  [
    "Where can I check previous Bhutan Morning Teer results?",
    "You can check past results on the Previous Result page, which lists earlier dates with their F/R and S/R numbers, newest first.",
  ],
  [
    "How do I check the FR and SR results?",
    "Simply open this homepage. Today's F/R and S/R numbers are shown in the result card near the top as soon as they are published.",
  ],
  [
    "Are results updated every day?",
    "Yes. The result is updated each day after it is officially published. Until then, the card shows “Waiting for Result”.",
  ],
];

function PreviewCard({ title, to, linkText, Icon, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-base font-bold text-[#10161f]">
          <Icon size={18} className="text-[#1ab5a3]" /> {title}
        </h2>
        <Link
          to={to}
          className="flex items-center gap-1 text-sm font-semibold text-[#0f9b8a] hover:underline"
        >
          {linkText} <ArrowRight size={14} />
        </Link>
      </div>
      {children}
    </section>
  );
}

export default function Home() {
  const date = todayDDMMYYYY("-");
  const { data: today } = useQuery({ queryKey: ["today-result"], queryFn: getTodayResult });
  const { data: prev } = useQuery({
    queryKey: ["previous-results", "home"],
    queryFn: () => getPreviousResults({ page: 1, limit: 8 }),
  });
  const { data: cn } = useQuery({ queryKey: ["common-number"], queryFn: getLatestCommonNumber });

  const prevItems = prev?.items || [];
  const common = cn?.available ? cn.data : null;
  const dreamPreview = DREAM_CHART.slice(0, 6);

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map(([q, a]) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };

  return (
    <Layout>
      <Seo
        title="Bhutan Morning Teer Result Today | FR & SR Result"
        description="Check Bhutan Morning Teer Result Today with the latest FR and SR results, result timings, previous results, common numbers and dream numbers."
        path="/"
        breadcrumb={[{ name: "Home", path: "/" }]}
        jsonLd={faqJsonLd}
      />

      <div className="py-4">
        <h1 className="mb-3 px-4 text-center text-2xl font-extrabold text-[#10161f] sm:text-3xl">
          Bhutan Morning Teer Result Today
        </h1>

        <p
          data-testid="current-date"
          className="mb-3 text-center text-lg font-bold tracking-wide text-slate-600"
        >
          {date}
        </p>

        <ResultBox result={today?.result} declared={today?.declared} date={date} />

        {/* Quick navigation — crawlable anchor links */}
        <nav
          data-testid="quick-nav"
          className="mx-auto mt-4 flex max-w-xl flex-wrap justify-center gap-2 px-3"
          aria-label="Quick navigation"
        >
          <Link to="/previous-result/" className="rounded-full bg-[#1ab5a3] px-4 py-1.5 text-sm font-semibold text-white hover:bg-[#0f9b8a]">
            Previous Results
          </Link>
          <Link to="/common-number/" className="rounded-full bg-[#16406e] px-4 py-1.5 text-sm font-semibold text-white hover:opacity-90">
            Common Number
          </Link>
          <Link to="/dream-number/" className="rounded-full bg-[#f1584c] px-4 py-1.5 text-sm font-semibold text-white hover:opacity-90">
            Dream Number
          </Link>
        </nav>

        <div className="mt-6">
          <CardGrid />
        </div>

        <div className="mx-auto mt-8 max-w-2xl space-y-6 px-4">
          {/* About */}
          <section>
            <h2 className="mb-2 text-lg font-bold text-[#10161f]">
              About Today's Bhutan Morning Teer Result
            </h2>
            <p className="text-sm leading-relaxed text-slate-600">
              This website is a simple, fast place to check the Bhutan Morning
              Teer Result Today. As soon as the result is officially declared, the
              First Round (F/R) and Second Round (S/R) numbers appear in the
              result card at the top of this page. You can also browse the full
              history of earlier results on the Previous Result page, and look up
              common numbers and dream numbers using the links above. Results are
              shown here only after they are published — never before.
            </p>
          </section>

          {/* Result timing */}
          <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-[#10161f]">
              <Clock size={18} className="text-[#1ab5a3]" /> Bhutan Morning Teer
              Result Time
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-slate-50 py-3 text-center">
                <div className="text-xs font-semibold text-slate-500">First Round (F/R)</div>
                <div className="mt-1 text-xl font-extrabold text-[#10161f]">10:30 AM</div>
              </div>
              <div className="rounded-lg bg-slate-50 py-3 text-center">
                <div className="text-xs font-semibold text-slate-500">Second Round (S/R)</div>
                <div className="mt-1 text-xl font-extrabold text-[#10161f]">11:30 AM</div>
              </div>
            </div>
          </section>

          {/* Previous results preview */}
          <PreviewCard
            title="Recent Previous Results"
            to="/previous-result/"
            linkText="View All"
            Icon={ListChecks}
          >
            {prevItems.length === 0 ? (
              <p className="text-sm text-slate-500">Previous results will appear here.</p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-slate-200">
                <table className="w-full text-center text-sm">
                  <thead>
                    <tr className="bg-[#1ab5a3] text-white">
                      <th className="py-2 font-semibold">Date</th>
                      <th className="py-2 font-semibold">F/R</th>
                      <th className="py-2 font-semibold">S/R</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prevItems.map((r) => (
                      <tr key={r.id} className="odd:bg-white even:bg-[#f5fbfa]">
                        <td className="border-t border-slate-100 py-2 text-[#10161f]">{r.date}</td>
                        <td className="border-t border-slate-100 py-2 font-bold">{r.fr}</td>
                        <td className="border-t border-slate-100 py-2 font-bold">{r.sr}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </PreviewCard>

          {/* Common number preview */}
          <PreviewCard
            title="Common Number"
            to="/common-number/"
            linkText="View Common Number"
            Icon={Hash}
          >
            {common ? (
              <p className="text-sm text-slate-600">
                Latest common number for <b>{common.date}</b> ({common.game}):{" "}
                Direct <b>{common.rows?.[0]?.direct || "-"}</b>,&nbsp;House{" "}
                <b>{common.rows?.[0]?.house || "-"}</b>,&nbsp;Ending{" "}
                <b>{common.rows?.[0]?.ending || "-"}</b>.
              </p>
            ) : (
              <p className="text-sm text-slate-500">Common number will appear here.</p>
            )}
          </PreviewCard>

          {/* Dream number preview */}
          <PreviewCard
            title="Dream Number"
            to="/dream-number/"
            linkText="View Dream Number"
            Icon={CloudMoon}
          >
            <div className="flex flex-wrap gap-2">
              {dreamPreview.map((d) => (
                <span key={d.sl} className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs text-slate-700">
                  {d.dream}: <b>{d.direct || d.ending || d.house || "-"}</b>
                </span>
              ))}
            </div>
          </PreviewCard>

          {/* FAQ */}
          <section>
            <h2 className="mb-3 text-lg font-bold text-[#10161f]">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible data-testid="home-faq">
              {FAQS.map(([q, a], i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="mb-2 overflow-hidden rounded-xl border border-slate-200 bg-white"
                >
                  <AccordionTrigger className="px-4 text-left text-[15px] font-semibold text-[#10161f] hover:no-underline">
                    {q}
                  </AccordionTrigger>
                  <AccordionContent className="px-4 text-[15px] leading-relaxed text-slate-600">
                    {a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* Disclaimer */}
          <section className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <h2 className="mb-1 text-sm font-bold text-amber-800">Disclaimer</h2>
            <p className="text-xs leading-relaxed text-amber-800/90">
              This website provides Bhutan Morning Teer result information for
              informational purposes only. We do not guarantee predictions,
              winnings, or any specific outcome. Please follow applicable local
              laws and rules.
            </p>
          </section>
        </div>
      </div>
    </Layout>
  );
}
